import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import OnboardingStepFour from 'src/app/Onboarding/OnboardingStepFour'
import OnboardingStepOne from 'src/app/Onboarding/OnboardingStepOne'
import type { OnboardingStepDisplayMode } from 'src/app/Onboarding/onboardingSteps'
import { computeOnboardingStepDisplay } from 'src/app/Onboarding/onboardingSteps'
import OnboardingStepThree from 'src/app/Onboarding/OnboardingStepThree'
import OnboardingStepThreeJPM from 'src/app/Onboarding/OnboardingStepThreeJPM'
import OnboardingStepTwo from 'src/app/Onboarding/OnboardingStepTwo'
import { selectPatchingOrganizationInviteFromState } from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { selectErrorDetailMapFromState } from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import {
  AccountBinInputPageCvcFlatFields,
  AccountBinInputPageFlatFields,
} from 'src/app/RegisterAccount/RegisterAccount'
import { selectIsSigningInFromState } from 'src/app/Signin/redux/signin.selectors'
import AppName from 'src/components/AppName/AppName'
import BrandAgnosticFooter from 'src/components/BrandAgnosticFooter/BrandAgnosticFooter'
import BrandPrimaryLogo from 'src/components/BrandPrimaryLogo/BrandPrimaryLogo'
import CobrandLogo from 'src/components/CobrandLogo/CobrandLogo'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import OnboardingFlowOrgInviteErrorBanner from 'src/components/OnboardingFlowOrgInviteErrorBanner/OnboardingFlowOrgInviteErrorBanner'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import SvgLocked from 'src/components/Svgs/SvgLocked'
import UnauthedBox from 'src/components/UnauthedBox/UnauthedBox'
import UnauthedBoxContainer from 'src/components/UnauthedBoxContainer/UnauthedBoxContainer'
import UnauthedPageContainer from 'src/components/UnauthedPageContainer/UnauthedPageContainer'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { SupportedBrands } from 'src/enums'
import {
  FormFieldsValidation,
  useFormFields,
  useScrollY,
  useWindowSize,
} from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import { connectedReplace } from 'src/navigation/Lnk'
import { homeUrl } from 'src/navigation/urls'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import {
  selectNullSafeLocaleFromState,
  selectOrgInviteErrorFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectMostRecentlyCreatedCreditCardListModelFromState } from 'src/redux/selectors/data/creditCards.selectors'
import {
  selectAppName,
  selectBrandName,
} from 'src/redux/selectors/session.selectors'
import { resetModalScroll } from 'src/utils/modalScroll.utils'
import {
  stringIsShortDate,
  stringIsShortDateInFuture,
} from 'src/utils/validation/date.validation'
import {
  cvcIsEmpty,
  stringIsEmpty,
  stringIsLuhnValid,
  stringLengthIsBetween,
  stringLengthIsLessThan,
} from 'src/utils/validation/string.validation'
import { useWhitelabel } from 'src/utils/whitelabel.utils'
import { css } from 'styled-components'

export type CompanyNameFormFlatFields = {
  companyLegalName?: string
}

export type CompanyInfoFormFlatFields = {
  industry?: string
  teamSize?: string
  department?: string
  referralSource?: string
  referralName?: string
}

export type PaymentNetInputPageFlatFields = {
  paymentNetOrgId?: string
  paymentNetUserId?: string
  paymentGroupId?: string
  last4?: string
}

type OnboardingProps = {
  /**
   * The step the flow should land on when first mounted. Defaults to 1.
   * Used by the `/onboarding/card` deep-link entry point (EX-39432) to drop
   * users straight into the card-registration step after a drop-off.
   */
  initialStep?: number
  stepDisplayMode?: OnboardingStepDisplayMode
}

const Onboarding: React.FC<OnboardingProps> = ({
  initialStep = 1,
  stepDisplayMode = 'fullFlow',
}) => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const appName = useSelector(selectAppName)
  const user = useCurrentUser()
  const mostRecentCreditCard = useSelector(
    selectMostRecentlyCreatedCreditCardListModelFromState,
  )
  const brandName = useSelector(selectBrandName)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const orgInviteError = useSelector(selectOrgInviteErrorFromState)
  const isSigningIn = useSelector(selectIsSigningInFromState)
  const isPatchingOrganizationInvite = useSelector(
    selectPatchingOrganizationInviteFromState,
  )
  const { whiteLabelIncludes } = useWhitelabel()

  const [step, setStep] = useState(initialStep)
  const [scrollY] = useScrollY()
  const [windowSize] = useWindowSize()
  const [hideLogo, setHideLogo] = useState(true)
  const [timeoutActive, setTimeoutActive] = useState(true)

  const translate = useTranslate()

  const boxRef = useRef<HTMLDivElement | undefined>()
  const imgRef = useRef<HTMLDivElement | undefined>()
  const onboardingScrollContainerRef = useRef<HTMLDivElement | null>(null)

  const logoLeftOffset = 30
  const logoTopOffset = 30

  const stepDisplay = computeOnboardingStepDisplay({
    currentStep: step,
    initialStep,
    stepDisplayMode,
  })

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(
      getCreditCards({
        count: 10,
        page: 0,
        type: CreditCardType.Source,
        sortField: 'activeUpdatedAt',
      }),
    )
  }, [])

  // redirect users if they have an org
  useEffect(() => {
    if (user?.isInExplicitOrg && step !== 3 && step !== 4) {
      dispatch(connectedReplace(homeUrl()))
    }
  }, [user, step])

  const {
    fields: companyNameFlatFields,
    fieldsErrors: companyNameFlatFieldsErrors,
    onFieldChange: onCompanyNameFlatFieldChange,
    setErrorsAndReturnDidPassValidation: companyNameFlatFieldsSetErrors,
  } = useFormFields<CompanyNameFormFlatFields>(
    {
      companyLegalName: mostRecentCreditCard?.companyName,
    },
    () => ({
      companyLegalName: [stringIsEmpty(), stringLengthIsLessThan(45)],
    }),
    undefined,
    undefined,
  )

  // need this here because of timing
  useEffect(() => {
    onCompanyNameFlatFieldChange(
      'companyLegalName',
      mostRecentCreditCard?.companyName,
    )
  }, [mostRecentCreditCard?.companyName])

  const {
    fields: companyInfoFlatFields,
    fieldsErrors: companyInfoFlatFieldsErrors,
    onFieldChange: onCompanyInfoFlatFieldChange,
    setErrorsAndReturnDidPassValidation: companyInfoFlatFieldsSetErrors,
    setFieldsErrors: setCompanyInfoFlatFieldsErrors,
  } = useFormFields<CompanyInfoFormFlatFields>(
    {},
    () => ({
      industry: [stringIsEmpty()],
      teamSize: [stringIsEmpty()],
      department: [stringIsEmpty()],
      referralSource: [stringIsEmpty()],
    }),
    undefined,
    undefined,
  )

  const {
    fields: jpmFlatFields,
    fieldsErrors: jpmFlatFieldsErrors,
    onFieldChange: onJpmFlatFieldChange,
    setErrorsAndReturnDidPassValidation: jpmFlatFieldsSetErrors,
  } = useFormFields<PaymentNetInputPageFlatFields>(
    {},
    () => ({
      paymentNetOrgId: [stringIsEmpty()],
      paymentNetUserId: [stringIsEmpty()],
      paymentGroupId: [stringIsEmpty()],
      last4: [stringIsEmpty()],
    }),
    undefined,
    undefined,
  )

  const accountBinInputPageFlatFieldsValidation =
    (): FormFieldsValidation<AccountBinInputPageFlatFields> => {
      return {
        cardNumber: [stringIsEmpty(), stringIsLuhnValid],
        expirationDate: [
          stringIsEmpty(),
          stringIsShortDate(locale),
          stringIsShortDateInFuture(locale),
        ],
      }
    }

  const {
    fields: accountBinInputPageFlatFields,
    fieldsErrors: accountBinInputPageFlatFieldsErrors,
    onFieldChange: onAccountBinInputPageFlatFieldChange,
    setErrorsAndReturnDidPassValidation: accountBinInputPageFlatFieldsSetErrors,
  } = useFormFields<AccountBinInputPageFlatFields>(
    {},
    accountBinInputPageFlatFieldsValidation,
    errorDetailMap,
  )

  const accountBinInputPageCvcFlatFieldsValidation =
    (): FormFieldsValidation<AccountBinInputPageCvcFlatFields> => {
      return {
        cvc: [cvcIsEmpty, stringLengthIsBetween(3, 4)],
      }
    }

  const {
    fields: accountBinInputPageCvcFlatFields,
    fieldsErrors: accountBinInputPageCvcFlatFieldsErrors,
    onFieldChange: onAccountBinInputPageCvcFlatFieldChange,
    setErrorsAndReturnDidPassValidation:
      accountBinInputPageCvcFlatFieldsSetErrors,
  } = useFormFields<AccountBinInputPageCvcFlatFields>(
    {},
    accountBinInputPageCvcFlatFieldsValidation,
    errorDetailMap,
  )

  const topLeftImage = (): React.ReactElement => {
    // update this to jpm logo
    if (
      brandName === SupportedBrands.Bmo ||
      brandName === SupportedBrands.Jpm
    ) {
      return (
        <div
          ref={imgRef as React.RefObject<HTMLDivElement>}
          css={css`
            margin-left: 30px;
            display: flex;
            flex-direction: row;
            align-items: center;
            ${brandName === SupportedBrands.Jpm &&
            css`
              margin-left: 48px;
              margin-top: 48px;
            `}
          `}
        >
          <CobrandLogo brandName={brandName} />
          {brandName === SupportedBrands.Bmo && (
            <div
              css={css`
                ${getFontCss(
                  12,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                margin-top: 10px;
                margin-left: 10px;
              `}
            >
              {translate('In collaboration with Extend')}
            </div>
          )}
        </div>
      )
    } else {
      return (
        <div
          ref={imgRef as React.RefObject<HTMLDivElement>}
          css={css`
            position: relative;
            top: ${logoTopOffset}px;
            left: ${logoLeftOffset}px;
            display: ${hideLogo ? css`none` : css`block`};
          `}
        >
          <BrandPrimaryLogo />
        </div>
      )
    }
  }

  const cardInfoHeader = (
    <div
      css={css`
        display: flex;
        flex-direction: row;
        margin-bottom: 20px;
      `}
    >
      <span
        css={css`
          display: flex;
          align-items: center;
          ${getFontCss(
            12,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        <SvgLocked
          css={css`
            transform-origin: left;
            transform: scale(calc(5 / 7));
          `}
        />
        {translate(
          'Our security controls meet the highest industry standards',
          {
            appName: <AppName />,
          },
        )}
      </span>
    </div>
  )

  // hide logo if center box obscures the logo
  useEffect(() => {
    if (imgRef.current !== undefined && boxRef.current !== undefined) {
      const imgChild = imgRef.current.children[0] as HTMLCanvasElement

      setHideLogo(
        logoLeftOffset + imgChild.width > boxRef.current.offsetLeft &&
          (logoTopOffset + imgChild.height <= window.scrollY ||
            logoTopOffset + imgChild.height > boxRef.current.offsetTop),
      )
    }
  }, [scrollY, windowSize])

  const resetOnboardingScroll = useCallback((): void => {
    if (onboardingScrollContainerRef.current) {
      onboardingScrollContainerRef.current.scrollTop = 0
    }
    resetModalScroll()
  }, [])

  const setStepAndResetScroll = (nextStep: number): void => {
    resetOnboardingScroll()
    setStep(nextStep)
  }

  const setNextStep = (num?: number): void => {
    if (num) {
      setStepAndResetScroll(num)
    } else {
      setStepAndResetScroll(step + 1)
    }
  }

  const stepComponents = (): React.ReactElement => {
    switch (step) {
      case 1:
        return (
          <OnboardingStepOne
            currentStep={stepDisplay.displayStep}
            totalSteps={stepDisplay.totalSteps}
            setStep={setNextStep}
            user={user}
            mostRecentCreditCard={mostRecentCreditCard}
            flatFields={companyNameFlatFields}
            onFlatFieldChange={onCompanyNameFlatFieldChange}
            flatFieldsErrors={companyNameFlatFieldsErrors}
            setErrorsAndReturnDidPassValidation={companyNameFlatFieldsSetErrors}
            customColor={whiteLabelIncludes(SupportedBrands.Jpm)}
          />
        )
      case 2:
        return (
          <OnboardingStepTwo
            currentStep={stepDisplay.displayStep}
            totalSteps={stepDisplay.totalSteps}
            setStep={setNextStep}
            companyLegalName={companyNameFlatFields['companyLegalName']}
            flatFields={companyInfoFlatFields}
            onFlatFieldChange={onCompanyInfoFlatFieldChange}
            flatFieldsErrors={companyInfoFlatFieldsErrors}
            setErrorsAndReturnDidPassValidation={companyInfoFlatFieldsSetErrors}
            setErrors={setCompanyInfoFlatFieldsErrors}
            jpm={whiteLabelIncludes(SupportedBrands.Jpm)}
          />
        )
      case 3:
        return whiteLabelIncludes(SupportedBrands.Jpm) ? (
          <OnboardingStepThreeJPM
            currentStep={stepDisplay.displayStep}
            totalSteps={stepDisplay.totalSteps}
            setStep={setNextStep}
            flatFields={jpmFlatFields}
            onFlatFieldChange={onJpmFlatFieldChange}
            flatFieldsErrors={jpmFlatFieldsErrors}
            setErrorsAndReturnDidPassValidation={jpmFlatFieldsSetErrors}
            title={translate('Register an account!')}
            subtitle={translate(
              'Provide your J.P. Morgan PaymentNet information below to register an account. This account will be used to pay for expenses made with J.P. Morgan Virtual Cards.',
            )}
            cardInfoHeader={cardInfoHeader}
          />
        ) : (
          <OnboardingStepThree
            currentStep={stepDisplay.displayStep}
            totalSteps={stepDisplay.totalSteps}
            setStep={setStepAndResetScroll}
            flatFields={accountBinInputPageFlatFields}
            onFlatFieldChange={onAccountBinInputPageFlatFieldChange}
            flatFieldsErrors={accountBinInputPageFlatFieldsErrors}
            setErrorsAndReturnDidPassValidation={
              accountBinInputPageFlatFieldsSetErrors
            }
            cvcFlatFields={accountBinInputPageCvcFlatFields}
            onCvcFlatFieldChange={onAccountBinInputPageCvcFlatFieldChange}
            cvcFlatFieldsErrors={accountBinInputPageCvcFlatFieldsErrors}
            cvcSetErrorsAndReturnDidPassValidation={
              accountBinInputPageCvcFlatFieldsSetErrors
            }
            title={translate('Add your company credit card')}
            subtitle={
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 20px;
                `}
              >
                <p>
                  {translate(
                    'Enter your company credit card details in order to track expenses and control spending.',
                  )}
                </p>
                <p>
                  {translate(
                    "If you need help or don't have a valid card number from your bank, please reach out via our ${resourceCenter}.",
                    {
                      resourceCenter: (
                        <ResourceCenterLink
                          size={HoverableLinkSizes.Large}
                          analytics={{
                            event: 'register card - contact support',
                            properties: {
                              intent: 'contact support',
                              interaction: 'text link press',
                            },
                          }}
                          underline
                          regular
                        />
                      ),
                    },
                  )}
                </p>
              </div>
            }
            cardInfoHeader={cardInfoHeader}
            cvcPlaceholder={'###'}
          />
        )
      case 4:
        return (
          <OnboardingStepFour
            currentStep={stepDisplay.displayStep}
            totalSteps={stepDisplay.totalSteps}
            onResetScroll={resetOnboardingScroll}
          />
        )
      default:
        return <></>
    }
  }

  // adding a timer to prevent the onboarding screen from flashing if user accepts invite
  setTimeout(() => {
    setTimeoutActive(false)
  }, 2000)

  const isInOrgLoadingState = user?.isInExplicitOrg && step !== 3 && step !== 4
  return (
    <>
      {isSigningIn ||
      isPatchingOrganizationInvite ||
      isInOrgLoadingState ||
      timeoutActive ? (
        <LoadingBackgroundPage />
      ) : (
        <div
          css={css`
            width: 100%;
          `}
        >
          {/* TODO: re-arrange helmets among step pages */}
          <Helmet>
            <title>{`Get started today and create virtual cards with the card in your pocket. | ${appName}`}</title>
            <meta
              name="description"
              content={`Create an ${appName} account to create, send, and manage virtual cards for all your business payments.`}
            />
          </Helmet>
          <UnauthedPageContainer
            topLeftImage={topLeftImage()}
            topLeftImageScale={1.0}
            scrollContainerRef={onboardingScrollContainerRef}
          >
            <UnauthedBoxContainer
              css={css`
                display: flex;
                padding: 120px;
                ${getBreakpointDown(breakpointSizes.XSM)(css`
                  padding: 120px 10px;
                `)}
              `}
              noBox
              hideBrandLogo
              footer={
                <div
                  css={css`
                    display: flex;
                    justify-content: center;
                    z-index: 1;
                  `}
                >
                  <BrandAgnosticFooter
                    css={css`
                      flex-grow: 1;
                      width: auto;
                    `}
                    logOutOption
                    step={step}
                  />
                </div>
              }
            >
              <div ref={boxRef as React.RefObject<HTMLDivElement>}>
                {orgInviteError && step === 1 && (
                  <OnboardingFlowOrgInviteErrorBanner />
                )}
                <UnauthedBox
                  css={css`
                    padding: 0px;
                    max-width: none;
                    width: 100%;
                    z-index: 2;
                  `}
                  noInnerPadding
                >
                  {stepComponents()}
                </UnauthedBox>
              </div>
            </UnauthedBoxContainer>
          </UnauthedPageContainer>
        </div>
      )}
    </>
  )
}

export default Onboarding
